/* eslint no-param-reassign: ["error", { "props": false }] */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk } from "app/store";
import { getBookings, Booking } from "api/bookings";
import { Club } from "api/clubs";
import { getCompanySubscriptons, Subscription } from "api/company";
import moment from "moment";
import { setPersistedData, PersistedData } from "utils/persist";

// calendar state
interface CalendarState {
    bookings: Booking[],
    club: Club | null,
    subscription: Subscription | null,
    date: string,
    error: string | null,
    isLoading: boolean,
}

// initial state
const initialState: CalendarState = {
    bookings: [],
    club: null,
    subscription: null,
    date: moment().toISOString(),
    error: null,
    isLoading: false,
};

// generic reducers
const startLoading = (state: CalendarState) => {
    state.isLoading = true;
    state.error = null;
};
const loadingFailed = (state: CalendarState, { payload }: PayloadAction<string>) => {
    state.isLoading = false;
    state.error = payload;
};

// slice
const calendarSlice = createSlice({
    name: "calendar",
    initialState,
    reducers: {
        getBookingsStart: startLoading,
        getBookingsSuccess: (state, { payload }: PayloadAction<Booking[]>) => {
            state.isLoading = false;
            state.bookings = payload;
        },
        getBookingsFailure: loadingFailed,
        getClubSuccess: (state, { payload }: PayloadAction<Club>) => {
            state.isLoading = false;
            state.club = payload;
        },
        getSubscriptionStart: startLoading,
        getSubscriptionSuccess: (state, { payload }: PayloadAction<Subscription>) => {
            state.isLoading = false;
            state.subscription = payload;
        },
        getSubscriptionFailure: loadingFailed,
        setDate: (state, { payload }: PayloadAction<string>) => {
            state.date = payload;
        },
    },
});

// inferred actions
const { getBookingsStart, getBookingsSuccess, getBookingsFailure, getSubscriptionStart, getSubscriptionSuccess, getSubscriptionFailure } = calendarSlice.actions;
export const { getClubSuccess, setDate } = calendarSlice.actions;

// fetch bookings in a club on a day
export const fetchBookings = (clubId: string, date: string): AppThunk => async (dispatch) => {
    try {
        dispatch(getBookingsStart());
        const bookings = await getBookings(clubId, date);
        dispatch(getBookingsSuccess(bookings));
    } catch (error: any) {
        dispatch(getBookingsFailure(error.type));
    }
};

// fetch subscriptions
export const fetchSubscription = (clubId: string): AppThunk => async (dispatch) => {
    try {
        dispatch(getSubscriptionStart());
        const subscription: Subscription[] = await getCompanySubscriptons({ clubId });
        if (subscription.length >= 0) {
            dispatch(getSubscriptionSuccess(subscription[0]));
        }
    } catch (error: any) {
        dispatch(getSubscriptionFailure(error.type));
    }
};

// switch club
export const switchClub = (club: Club): AppThunk => (dispatch) => {
    setPersistedData(PersistedData.CurrentClub, club.id);
    dispatch(getClubSuccess(club));
};

export default calendarSlice.reducer;
